import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { FileText, BadgeCheck, ArrowLeft, ArrowRight, Lock, Clock, Video, CheckCircle2, Star, Search, HelpCircle, History } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "");

export default function Access() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get(`/access/${token}`)
      .then((r) => {
        setData(r.data);
        if (r.data.purchase?.status === "paid") localStorage.setItem("pp_access_token", token);
      })
      .catch(() => setError(true));
  }, [token]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-6" data-testid="access-invalid">
        <p className="font-display text-3xl font-bold">This access link isn't valid.</p>
        <p className="max-w-md text-center text-sm text-zinc-500">If you just paid, give it a minute — otherwise reach us through the contact form and we'll fix it.</p>
        <Link to="/" className="link-underline text-sm uppercase tracking-widest text-brand">Back to home</Link>
      </div>
    );
  }

  if (!data) {
    return <div className="flex min-h-screen items-center justify-center text-zinc-600" data-testid="access-loading">Unlocking your course...</div>;
  }

  const { purchase, course } = data;
  const isPaid = purchase.status === "paid";
  const progress = purchase.progress || {};
  const bookmarks = purchase.bookmarks || {};
  const attempts = purchase.quiz_attempts || [];
  const lessons = course.lessons || [];
  const doneCount = lessons.filter((l) => progress[l.id]).length;
  const pct = lessons.length ? Math.round((doneCount / lessons.length) * 100) : 0;
  const visible = lessons.filter((l) => l.title.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <div className="min-h-screen px-6 py-16 md:px-12 md:py-24" data-testid="access-page">
      <div className="mx-auto max-w-5xl">
        <Link to="/" data-testid="access-home-link" className="mb-12 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-zinc-500 hover:text-white">
          <ArrowLeft size={14} /> ppacedemy.in
        </Link>
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
          {isPaid ? (
            <div className="flex items-center gap-3 text-emerald-500">
              <BadgeCheck size={20} />
              <span className="text-xs font-bold uppercase tracking-widest" data-testid="access-confirmed-label">You're enrolled</span>
            </div>
          ) : purchase.status === "revoked" ? (
            <div className="flex items-center gap-3 text-red-400">
              <Lock size={20} />
              <span className="text-xs font-bold uppercase tracking-widest" data-testid="access-revoked-label">Enrollment revoked</span>
            </div>
          ) : (
            <div className="flex items-center gap-3 text-amber-500">
              <Clock size={20} />
              <span className="text-xs font-bold uppercase tracking-widest" data-testid="access-pending-label">Verifying your UPI payment</span>
            </div>
          )}
          <h1 className="mt-6 font-display text-4xl font-black uppercase leading-none tracking-tighter sm:text-5xl" data-testid="access-course-title">
            {course.title}
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-zinc-400">
            {isPaid
              ? `Welcome aboard, ${purchase.name}. Every chapter follows your textbook — read the simplified explanation, take the self-test, mark it complete.`
              : purchase.status === "revoked"
              ? `Hi ${purchase.name}, this enrollment was revoked — usually because the UPI payment didn't arrive. Pay again from the course page or message us on WhatsApp and we'll sort it out.`
              : `Thanks, ${purchase.name}. We received your details (UTR: ${purchase.payment_id}) and are verifying your UPI payment. Free preview chapters are open below; everything else unlocks once approved.`}
          </p>
        </motion.div>

        {isPaid && purchase.children?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8, ease: EASE }} className="mt-14" data-testid="bundle-subjects">
            <h2 className="font-display text-2xl font-black uppercase tracking-tighter">Your Semester Subjects</h2>
            <p className="mt-2 text-sm text-zinc-500">Each subject has its own dashboard — chapters, self-tests, progress and question bank.</p>
            <div className="mt-6 grid grid-cols-1 gap-px border border-white/10 bg-white/10 sm:grid-cols-2">
              {purchase.children.map((ch, i) => (
                <Link key={ch.access_token} to={`/access/${ch.access_token}`} data-testid={`bundle-subject-${i}`} className="group flex items-center justify-between gap-4 bg-[#0a0a0a] px-6 py-5 transition-colors duration-300 hover:bg-[#111111]">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-zinc-600">Subject {String(i + 1).padStart(2, "0")}</div>
                    <div className="mt-1 font-display text-base font-bold leading-tight tracking-tight md:text-lg">{ch.title}</div>
                  </div>
                  <ArrowRight size={16} className="shrink-0 text-zinc-600 transition-all duration-300 group-hover:translate-x-1 group-hover:text-brand" />
                </Link>
              ))}
            </div>
          </motion.div>
        )}

        {isPaid && lessons.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }} className="mt-10 border border-white/10 bg-[#111111] p-6" data-testid="progress-card">
            <div className="flex items-center justify-between text-xs uppercase tracking-widest text-zinc-500">
              <span>Syllabus progress</span>
              <span data-testid="progress-label">{doneCount}/{lessons.length} chapters · {pct}%</span>
            </div>
            <div className="mt-3 h-1.5 w-full bg-white/10">
              <div className="h-full bg-brand transition-[width] duration-700" style={{ width: `${pct}%` }} data-testid="progress-bar" />
            </div>
          </motion.div>
        )}

        {lessons.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8, ease: EASE }} className="mt-14">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h2 className="font-display text-2xl font-black uppercase tracking-tighter">Chapters</h2>
            <div className="relative">
              <Search size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search a topic..."
                data-testid="chapter-search-input"
                className="w-56 rounded-none border-b border-[#27272A] bg-transparent py-2 pl-6 text-sm text-white placeholder-zinc-600 outline-none transition-colors duration-300 focus:border-white"
              />
            </div>
          </div>
          <div className="mt-6 divide-y divide-white/10 border border-white/10" data-testid="access-lessons">
            {visible.map((l, i) => {
              const unlocked = isPaid || l.free_preview;
              const inner = (
                <>
                  <div className="flex items-center gap-4">
                    <span className="font-display text-sm font-bold text-zinc-600">{String(lessons.indexOf(l) + 1).padStart(2, "0")}</span>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-zinc-200 md:text-base">{l.title}</span>
                        {l.has_quiz && <HelpCircle size={14} className="text-brand" />}
                        {isPaid && progress[l.id] && <CheckCircle2 size={15} className="text-emerald-500" />}
                        {isPaid && bookmarks[l.id] && <Star size={14} className="fill-amber-400 text-amber-400" />}
                      </div>
                      <span className="text-xs text-zinc-600">
                        {l.duration}
                        {l.updated_at ? ` · Updated ${fmtDate(l.updated_at)}` : ""}
                      </span>
                    </div>
                  </div>
                  {!unlocked && <Lock size={16} className="shrink-0 text-zinc-600" />}
                  {!isPaid && l.free_preview && <span className="shrink-0 bg-emerald-500/10 px-2.5 py-1 text-xs font-bold uppercase tracking-widest text-emerald-500">Free</span>}
                </>
              );
              return unlocked ? (
                <Link key={l.id} to={`/access/${token}/learn/${l.id}`} data-testid={`access-lesson-${i}`} className="group flex items-center justify-between gap-4 px-6 py-5 transition-colors duration-300 hover:bg-white/5">
                  {inner}
                </Link>
              ) : (
                <div key={l.id} className="flex items-center justify-between gap-4 px-6 py-5 opacity-50" data-testid={`access-lesson-locked-${i}`}>
                  {inner}
                </div>
              );
            })}
            {visible.length === 0 && (
              <p className="px-6 py-8 text-sm text-zinc-500" data-testid="no-chapters-found">No chapters match "{query}".</p>
            )}
          </div>
        </motion.div>
        )}

        {isPaid && attempts.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8, ease: EASE }} className="mt-14" data-testid="quiz-history">
            <h2 className="font-display text-2xl font-black uppercase tracking-tighter">Your Test Scores</h2>
            <div className="mt-6 divide-y divide-white/10 border border-white/10">
              {[...attempts].reverse().map((a, i) => (
                <div key={i} className="flex items-center justify-between gap-4 px-6 py-4" data-testid={`quiz-attempt-${i}`}>
                  <span className="text-sm text-zinc-300">{a.lesson_title}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-zinc-600"><History size={11} className="mr-1 inline" />{fmtDate(a.at)}</span>
                    <span className={`font-display text-sm font-black ${a.score / a.total >= 0.6 ? "text-emerald-500" : "text-red-400"}`}>{a.score}/{a.total}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {course.question_bank?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.8, ease: EASE }} className="mt-14" data-testid="access-qbank">
            <h2 className="font-display text-2xl font-black uppercase tracking-tighter">Question Bank</h2>
            <p className="mt-2 text-sm text-zinc-500">Previous years' exam questions. <Star size={13} className="inline fill-amber-400 text-amber-400" /> = frequently repeated in board/university exams.</p>
            <div className="mt-6 divide-y divide-white/10 border border-white/10">
              {course.question_bank.map((q, i) => (
                <div key={q.id || i} className="px-6 py-5" data-testid={`qbank-item-${i}`}>
                  <div className="flex items-start gap-4">
                    {q.important ? <Star size={16} className="mt-0.5 shrink-0 fill-amber-400 text-amber-400" /> : <HelpCircle size={16} className="mt-0.5 shrink-0 text-zinc-600" />}
                    <div className="min-w-0">
                      <p className="text-sm text-zinc-200 md:text-base">{q.question}</p>
                      <p className="mt-1 text-xs uppercase tracking-widest text-zinc-600">{[q.year, q.topic].filter(Boolean).join(" · ")}</p>
                      {isPaid && q.answer && (
                        <p className="mt-2 border-l-2 border-brand/50 pl-3 text-sm text-zinc-400" data-testid={`qbank-answer-${i}`}>{q.answer}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {!isPaid && <p className="mt-3 text-xs uppercase tracking-widest text-zinc-600">Answers unlock once your payment is verified.</p>}
          </motion.div>
        )}

        {course.notes.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.8, ease: EASE }} className="mt-14">
            <h2 className="font-display text-2xl font-black uppercase tracking-tighter">PDF Notes</h2>
            <div className="mt-6 flex flex-col gap-3" data-testid="access-notes">
              {course.notes.map((n, i) =>
                isPaid ? (
                  <a
                    key={n.title}
                    href={n.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`access-note-${i}`}
                    className="flex items-center gap-3 border border-white/10 px-6 py-4 text-sm text-zinc-200 transition-colors duration-300 hover:bg-white/5"
                  >
                    <FileText size={16} className="text-brand" /> {n.title}
                  </a>
                ) : (
                  <div key={n.title} className="flex items-center gap-3 border border-white/10 px-6 py-4 text-sm text-zinc-500 opacity-50" data-testid={`access-note-locked-${i}`}>
                    <Lock size={16} className="text-zinc-600" /> {n.title}
                  </div>
                )
              )}
            </div>
          </motion.div>
        )}

        <div className="mt-14 flex flex-col justify-between gap-6 border border-white/10 bg-[#111111] p-8 md:flex-row md:items-center">
          <p className="text-sm leading-relaxed text-zinc-400">
            Fresh chapters are added as the course grows. For doubts, use the contact form on the homepage and mention your course — every question reaches the mentor directly.
          </p>
          <Link to="/live" data-testid="access-live-link" className="flex shrink-0 items-center justify-center gap-2 border border-brand px-6 py-3 text-xs font-bold uppercase tracking-widest text-brand transition-colors duration-300 hover:bg-brand hover:text-white">
            <Video size={14} /> Live Classes
          </Link>
        </div>
      </div>
    </div>
  );
}
